import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { HERO_METRICS, DOMAINS } from '../mock';

const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden pt-28 pb-24 lg:pt-36 lg:pb-32">
      {/* Background: soft gradient + grid */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-50 via-indigo-50/50 to-white" />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.35] [background-image:linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_75%)]"
      />
      {/* Blobs */}
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-indigo-300/30 blur-3xl -z-10" />
      <div className="absolute -top-16 right-0 w-[380px] h-[380px] rounded-full bg-cyan-300/25 blur-3xl -z-10" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Eyebrow */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3.5 py-1.5 text-[12.5px] font-medium text-slate-700 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Now accepting new engagements
            <Sparkles size={13} className="text-indigo-500" />
          </div>
        </div>

        <h1 className="mt-8 text-center font-display text-[44px] leading-[1.02] sm:text-[56px] lg:text-[76px] font-semibold tracking-tight text-slate-900">
          Build systems that
          <br className="hidden sm:block" />{' '}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              refuse to break.
            </span>
            <svg
              aria-hidden
              viewBox="0 0 300 12"
              className="absolute -bottom-2 left-0 w-full text-indigo-300"
              preserveAspectRatio="none"
            >
              <path
                d="M2 8 C 80 -2, 180 14, 298 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </h1>

        <p className="mt-7 text-center text-[17px] md:text-[19px] leading-relaxed text-slate-600 max-w-2xl mx-auto">
          Scatura is a boutique engineering studio for teams serious about
          <span className="text-slate-900 font-medium"> intelligent automation</span>,
          <span className="text-slate-900 font-medium"> chaos engineering</span>,
          <span className="text-slate-900 font-medium"> self-healing systems</span> and
          <span className="text-slate-900 font-medium"> app modernization</span>. We prove value in weeks, then embed with your teams.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="#contact"
            className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/15"
          >
            Book a discovery call
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#services"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-slate-200 text-slate-800 font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors"
          >
            See what we do
          </a>
        </div>

        {/* Metrics strip */}
        <div className="mt-16 text-center">
          <p className="text-[12px] font-medium tracking-[0.2em] text-slate-400 uppercase">
            Founder-led delivery experience
          </p>
          <p className="mt-1 text-[13px] text-slate-500">
            Track record from prior engagements
          </p>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {HERO_METRICS.map((m) => (
            <div
              key={m.label}
              className="rounded-2xl border border-slate-200/80 bg-white/70 backdrop-blur px-5 py-5 text-center"
            >
              <div className="font-display text-[30px] font-semibold text-slate-900 tracking-tight">
                {m.value}
              </div>
              <div className="mt-1 text-[13px] text-slate-500">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Domains strip */}
        <div className="mt-16 text-center">
          <p className="text-[12px] font-medium tracking-[0.2em] text-slate-400 uppercase">
            Domain applicability
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
            {DOMAINS.map((d) => (
              <span
                key={d}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3.5 py-1.5 text-[13px] font-medium text-slate-700 hover:border-slate-300 hover:bg-white transition-colors"
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
